import type { Bulletin } from '@shared/types/Bulletin';
import type { Tenant } from '@shared/types/Tenant';
import type {
  EtatReversementIRPP,
  ReversementIRPPRecord,
  StatutReversementIRPP,
} from '@shared/types/ReversementIRPP';
import { roundFCFA } from '@shared/utils/round';
import { retenirBulletinsUniques } from '@domain/paie/BulletinsUniques';

const PERIODE_RE = /^(\d{4})-(\d{2})$/;

function bulletinEligible(b: Bulletin): boolean {
  return b.statut === 'valide' || b.statut === 'paye';
}

function echapperCsv(value: string): string {
  return /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Reversement mensuel de l’IRPP prélevé à la source (FISC-B-01).
 * Obligation : reverser à la DGI avant le **15 du mois suivant** (CGI Art. 100 / DAMS).
 */
export class ReversementIRPPService {
  /**
   * Date limite = 15 du mois civil suivant la période de paie.
   * Ex. période `2026-03` → `2026-04-15`.
   */
  static dateLimite(periode: string): string {
    const m = PERIODE_RE.exec(periode);
    if (!m) throw new Error('Période invalide (attendu YYYY-MM)');
    const annee = Number(m[1]);
    const mois = Number(m[2]);
    if (mois < 1 || mois > 12) throw new Error('Période invalide (mois 01–12)');
    const next = new Date(annee, mois, 15); // mois JS = mois civil suivant (0-index)
    return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-15`;
  }

  static statut(
    dateLimite: string,
    dateReversement: string | undefined,
    now = new Date(),
  ): StatutReversementIRPP {
    if (dateReversement) return 'reverse';
    const [y, mo, d] = dateLimite.split('-').map(Number);
    const limite = new Date(y!, mo! - 1, d!);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (today.getTime() > limite.getTime()) return 'en_retard';
    return 'a_reverser';
  }

  static genererEtat(
    bulletins: readonly Bulletin[],
    periode: string,
    tenant: Tenant | null | undefined,
    record?: ReversementIRPPRecord | null,
    now = new Date(),
  ): EtatReversementIRPP {
    // Un seul bulletin par salarié et par période (doublons historiques ignorés, jamais supprimés).
    const retenus = retenirBulletinsUniques(
      bulletins.filter((b) => b.periode === periode && bulletinEligible(b)),
    );
    const montant = roundFCFA(retenus.reduce((s, b) => s + (b.irpp_mensuel || 0), 0));
    const dateLimite = this.dateLimite(periode);
    const dateReversement = record?.date_reversement;
    return {
      tenant_id: tenant?.id ?? record?.tenant_id ?? '',
      periode,
      montant_irpp: montant,
      effectif: retenus.length,
      date_limite: dateLimite,
      statut: this.statut(dateLimite, dateReversement, now),
      date_reversement: dateReversement,
      reference_paiement: record?.reference_paiement,
    };
  }

  /** Bordereau CSV : cartouche employeur + totaux IRPP + échéance. */
  static exporterCsv(
    etat: EtatReversementIRPP,
    entreprise?: Tenant | null,
    lignes?: readonly { matricule: string; nom: string; irpp: number }[],
  ): string {
    const sep = ';';
    const libStatut: Record<StatutReversementIRPP, string> = {
      a_reverser: 'À reverser',
      reverse: 'Reversé',
      en_retard: 'En retard',
    };
    const cartouche: string[] = [
      ['Déclaration', 'Reversement IRPP (DAMS)'].join(sep),
      ['Période paie', etat.periode].join(sep),
      ['Employeur', echapperCsv(entreprise?.raison_sociale || entreprise?.nom || '')].join(sep),
      ['NIF', echapperCsv(entreprise?.nif || '')].join(sep),
      ['Montant IRPP', String(etat.montant_irpp)].join(sep),
      ['Date limite', etat.date_limite].join(sep),
      ['Statut', libStatut[etat.statut]].join(sep),
      ['Effectif', String(etat.effectif)].join(sep),
      '',
    ];
    if (!lignes || lignes.length === 0) {
      return cartouche.join('\n');
    }
    const header = ['Matricule', 'Salarié', 'IRPP'].join(sep);
    const rows = lignes.map((l) =>
      [echapperCsv(l.matricule), echapperCsv(l.nom), l.irpp].join(sep),
    );
    const pied = ['TOTAL', '', etat.montant_irpp].join(sep);
    return [...cartouche, header, ...rows, pied].join('\n');
  }

  static nomFichier(periode: string): string {
    return `reversement_irpp_${periode}.csv`;
  }

  /** Lignes nominatives pour export (bulletins éligibles de la période). */
  static lignesNominatives(
    bulletins: readonly Bulletin[],
    salaries: readonly { id: string; matricule?: string; prenom?: string; nom?: string }[],
    periode: string,
  ): { matricule: string; nom: string; irpp: number }[] {
    const parId = new Map(salaries.map((s) => [s.id, s]));
    return retenirBulletinsUniques(
      bulletins.filter((b) => b.periode === periode && bulletinEligible(b)),
    )
      .map((b) => {
        const sal = parId.get(b.salarie_id);
        const nom =
          b.nom_complet ||
          (sal ? `${sal.prenom ?? ''} ${sal.nom ?? ''}`.trim() : '') ||
          b.matricule ||
          '—';
        return {
          matricule: b.matricule || sal?.matricule || '',
          nom,
          irpp: roundFCFA(b.irpp_mensuel || 0),
        };
      })
      .sort((a, b) => a.matricule.localeCompare(b.matricule, 'fr'));
  }
}
